import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOPICS = [
  { id: "01", slug: "newcomers", label: "Newcomer Essentials", desc: "Your first months in Canada, sorted. SIN, first bank account, credit from zero, and what to skip." },
  { id: "02", slug: "budgeting", label: "Budgeting", desc: "Honest, flexible budgeting that fits real Canadian life: rent, groceries, transit, family support." },
  { id: "03", slug: "credit-building", label: "Credit Building", desc: "How credit scores actually work in Canada, the right tools to start with, and the habits that move your score the fastest." },
  { id: "04", slug: "mortgages", label: "Mortgages", desc: "Down payments, stress tests, fixed vs. variable, CMHC insurance — what buyers actually need to understand." },
  { id: "05", slug: "insurance", label: "Insurance", desc: "Health, tenant, auto, life, and disability — what's essential in Canada, what's optional, and how to avoid being oversold." },
  { id: "06", slug: "cost-of-living", label: "Cost of Living", desc: "Honest monthly cost breakdowns for Toronto, Vancouver, Calgary, Montreal, and beyond." },
];

const STATS = [
  { num: "6", label: "Pillar topics" },
  { num: "40+", label: "Research-based articles" },
  { num: "0", label: "Sponsored upsells" },
];

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscribe`, { email });
      setStatus({ type: "success", msg: res.data.message });
      setEmail("");
    } catch (err) {
      setStatus({ type: "error", msg: err.response?.data?.detail || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#1B4332] py-20 px-6" data-testid="newsletter-section">
      <div className="max-w-2xl mx-auto text-center space-y-6">
        <p className="text-[#D4A373] text-xs tracking-[0.2em] uppercase font-semibold">The Newsletter</p>
        <h2 className="text-white font-bold text-3xl sm:text-4xl leading-tight font-display">
          One careful email. Twice a month.
        </h2>
        <p className="text-[#A8C5B5] text-base leading-relaxed">
          The best new article, one Canadian money idea worth your time, and a practical tip you can apply this week. That's it. Unsubscribe anytime.
        </p>
        {status ? (
          <div
            data-testid="home-subscribe-status"
            className={`py-4 px-6 rounded-md font-medium ${
              status.type === "success"
                ? "bg-[#2D6A4F] text-[#D4A373]"
                : "bg-red-900/30 text-red-300"
            }`}
          >
            {status.msg}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="home-subscribe-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              required
              data-testid="home-email-input"
              className="flex-1 bg-[#2D6A4F] border border-[#40916C] text-white placeholder-[#A8C5B5] px-4 py-3 rounded-md text-sm focus:outline-none focus:border-[#D4A373] transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="home-subscribe-btn"
              className="bg-[#D4A373] hover:bg-[#E6B88A] text-[#1B4332] font-bold px-6 py-3 rounded-md transition-colors whitespace-nowrap disabled:opacity-60"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        )}
        <p className="text-[#A8C5B5] text-xs">No spam · No affiliate gimmicks · Read by Canadian families</p>
      </div>
    </section>
  );
}

export default function Home() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/articles?limit=6`).then((r) => setArticles(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="bg-[#F9F8F6] py-20 md:py-28 px-6 border-b border-[#E7E5E4]" data-testid="hero-section">
        <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-fade-in-up">
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-[#D4A373]" />
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373]">
                Issue · {new Date().toLocaleDateString("en-CA", { month: "long", year: "numeric" })} · Vol. 01
              </p>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1C1C] leading-[1.05] tracking-tight">
              Smart money decisions for newcomers and Canadian families.
            </h1>
            <p className="text-[#57534E] text-lg leading-relaxed max-w-lg">
              I'm Michael. I break down budgeting, credit, mortgages, insurance and cost of living using clear, research-based guidance. Nothing over-produced — just real-life, honest conversations about money in Canada.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/articles"
                data-testid="hero-read-latest"
                className="bg-[#1B4332] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#2D6A4F] transition-colors flex items-center gap-2"
              >
                Read the latest <ArrowRight size={16} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-michael"
                className="border border-[#1B4332] text-[#1B4332] font-semibold px-6 py-3 rounded-md hover:bg-[#1B4332] hover:text-white transition-colors"
              >
                About Michael
              </Link>
            </div>
            <p className="text-[#57534E] text-sm">Six topics · Plain English · No sales pitch</p>
          </div>
          <div className="relative rounded-xl overflow-hidden animate-fade-in stagger-2">
            <img
              src="https://images.pexels.com/photos/33769499/pexels-photo-33769499.jpeg"
              alt="Canadian families making smart financial decisions"
              className="w-full h-80 lg:h-[480px] object-cover rounded-xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1B4332]/60 to-transparent rounded-xl" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-[#D4A373] text-xs tracking-widest uppercase font-semibold mb-2">In this issue</p>
              <p className="text-white font-semibold text-lg leading-snug">
                Renting vs. Buying in 2025 — the math, not the vibes.
              </p>
              <Link
                to="/articles/renting-vs-buying-2025"
                className="text-[#D4A373] text-sm font-medium mt-2 inline-flex items-center gap-1 hover:gap-2 transition-all"
              >
                Read article <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Newcomer Essentials ─────────────────────────────────── */}
      <section className="py-16 px-6 bg-white border-b border-[#E7E5E4]" data-testid="newcomer-section">
        <div className="max-w-screen-xl mx-auto">
          <div className="mb-8">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-2">Start here</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1C1C1C]">
              Newcomer essentials — your first months in Canada.
            </h2>
            <p className="text-[#57534E] mt-3 text-base max-w-2xl">
              The setup nobody walks you through: SIN, first bank account, credit from zero, and what to skip in those first 90 days.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {articles.filter((a) => a.topic === "Newcomer Essentials").slice(0, 2).map((a) => (
              <Link
                key={a.id}
                to={`/articles/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="bg-white border border-[#E7E5E4] rounded-lg p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
              >
                <p className="topic-badge mb-3">{a.topic} · {a.read_time}</p>
                <h3 className="font-bold text-[#1C1C1C] text-lg leading-snug group-hover:text-[#1B4332] transition-colors mb-2">
                  {a.title}
                </h3>
                <p className="text-[#57534E] text-sm leading-relaxed line-clamp-2">{a.excerpt}</p>
                <p className="text-[#1B4332] text-sm font-medium mt-4 flex items-center gap-1">
                  Read article <ChevronRight size={14} />
                </p>
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link
              to="/topics/newcomers"
              className="text-[#1B4332] font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all"
              data-testid="all-newcomer-guides"
            >
              All newcomer guides <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Latest Articles ─────────────────────────────────────── */}
      <section className="py-16 px-6 bg-[#F9F8F6] border-b border-[#E7E5E4]" data-testid="articles-section">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-2">Latest writing</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1C1C1C]">
                Honest articles. Built around real Canadian numbers.
              </h2>
            </div>
            <Link
              to="/articles"
              className="hidden sm:flex text-[#1B4332] font-semibold text-sm items-center gap-1 hover:gap-2 transition-all"
              data-testid="all-articles-link"
            >
              All articles <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.slice(0, 6).map((a, i) => (
              <Link
                key={a.id}
                to={`/articles/${a.slug}`}
                data-testid={`home-article-${i}`}
                className="bg-white border border-[#E7E5E4] rounded-lg p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
              >
                <p className="topic-badge mb-3">{a.topic} · {a.read_time}</p>
                <h3 className="font-bold text-[#1C1C1C] text-base leading-snug group-hover:text-[#1B4332] transition-colors mb-2">
                  {a.title}
                </h3>
                <p className="text-[#57534E] text-sm leading-relaxed line-clamp-3">{a.excerpt}</p>
                <p className="text-[#57534E] text-xs mt-4">
                  {new Date(a.created_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}
                </p>
              </Link>
            ))}
          </div>
          <div className="mt-8 sm:hidden">
            <Link to="/articles" className="text-[#1B4332] font-semibold text-sm flex items-center gap-1">
              All articles <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Topics ──────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-white border-b border-[#E7E5E4]" data-testid="topics-section">
        <div className="max-w-screen-xl mx-auto">
          <div className="mb-8">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-2">Topics</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1C1C1C]">
              Six areas that cover most money decisions in Canada.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOPICS.map((t) => (
              <Link
                key={t.slug}
                to={`/topics/${t.slug}`}
                data-testid={`topic-card-${t.slug}`}
                className="border border-[#E7E5E4] rounded-lg p-6 hover:border-[#1B4332] hover:shadow-sm transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-[#D4A373] text-xs font-bold tracking-widest">{t.id}</span>
                  <ChevronRight size={16} className="text-[#E7E5E4] group-hover:text-[#1B4332] transition-colors" />
                </div>
                <h3 className="font-bold text-[#1C1C1C] mb-2 group-hover:text-[#1B4332] transition-colors">
                  {t.label}
                </h3>
                <p className="text-[#57534E] text-sm leading-relaxed">{t.desc}</p>
                <p className="text-[#1B4332] text-sm font-medium mt-4">Explore topic →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── About snippet ────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-[#F2EFE9] border-b border-[#E7E5E4]" data-testid="about-snippet">
        <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373]">About the author</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1C1C1C]">
              Independent. Research-based. Not selling you anything.
            </h2>
            <blockquote className="border-l-4 border-[#D4A373] pl-4 text-[#57534E] italic text-base leading-relaxed">
              "Every article on this site has to pass one test: would I send this to my own sister if she just landed in Canada?"
            </blockquote>
            <p className="text-[#57534E] text-sm">— Michael, founder · Mortgage associate & consultant</p>
            <div className="flex gap-8 pt-2">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-bold text-2xl text-[#1B4332]">{s.num}</p>
                  <p className="text-[#57534E] text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[#1B4332] font-semibold text-sm hover:gap-3 transition-all"
              data-testid="about-read-more"
            >
              Read Michael's story <ArrowRight size={14} />
            </Link>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_money-talk-michael/artifacts/38j7m4ke_Michael%27s%20photo.png"
              alt="Michael — Straight Talk Money"
              className="w-full h-80 lg:h-96 object-cover rounded-xl"
              onError={(e) => { e.target.src = "https://images.pexels.com/photos/4319926/pexels-photo-4319926.jpeg"; }}
            />
          </div>
        </div>
      </section>

      {/* ── Tools teaser ─────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-white border-b border-[#E7E5E4]" data-testid="tools-section">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-2">Tools</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1C1C1C]">
                Run the numbers on your own situation.
              </h2>
            </div>
            <Link to="/tools" className="hidden sm:flex text-[#1B4332] font-semibold text-sm items-center gap-1 hover:gap-2 transition-all">
              All tools <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { num: "01", label: "Mortgage affordability", desc: "What a lender will actually approve — stress test included.", slug: "mortgage" },
              { num: "02", label: "Monthly budget", desc: "A flexible budget that fits real Canadian living costs.", slug: "budget" },
              { num: "03", label: "Cost-of-living estimator", desc: "Compare monthly costs across Toronto, Vancouver, Calgary and more.", slug: "cost-of-living" },
              { num: "04", label: "Credit-builder timeline", desc: "See how long it takes to reach a 700+ score from zero.", slug: "credit-builder" },
            ].map((t) => (
              <Link
                key={t.slug}
                to={`/tools/${t.slug}`}
                data-testid={`tool-card-${t.slug}`}
                className="border border-[#E7E5E4] rounded-lg p-6 hover:border-[#1B4332] hover:shadow-sm transition-all duration-300 group"
              >
                <span className="text-[#D4A373] text-xs font-bold tracking-widest">Tool {t.num}</span>
                <h3 className="font-bold text-[#1C1C1C] mt-2 mb-2 capitalize group-hover:text-[#1B4332] transition-colors">
                  {t.label}
                </h3>
                <p className="text-[#57534E] text-sm leading-relaxed">{t.desc}</p>
                <p className="text-[#1B4332] text-sm font-medium mt-4">Open calculator →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Newsletter CTA ───────────────────────────────────────── */}
      <NewsletterSection />
    </div>
  );
}
